// src/api/DiscoveryPageApi.js

import axios from 'axios';
import { handleApiError } from './handleApiError'; // Assuming you have a centralized error handler

const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// --- NEW: Function to start the background search job ---
/**
 * Submits search criteria to the backend to start a background discovery job.
 * @param {string} token - The user's Firebase auth token.
 * @param {object} searchParams - The search parameters.
 * @returns {Promise<{status: boolean, data?: {jobId: string}, message?: string, error?: string}>}
 */
export const startDiscoverySearchJob = async (token, searchParams) => {
  const url = `${baseUrl}/discovery/api/start-search`;
  console.debug(
    '[DiscoveryPageApi.startDiscoverySearchJob] Firing request with params:',
    searchParams,
  );
  try {
    const response = await axios.post(url, searchParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 202) {
      // Expect 202 Accepted
      return { status: true, data: response.data };
    } else {
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'startDiscoverySearchJob');
  }
};

// --- NEW: Function to check the status of a background job ---
/**
 * Polls the backend for the status of a specific discovery job.
 * @param {string} token - The user's Firebase auth token.
 * @param {string} jobId - The ID of the job to check.
 * @returns {Promise<{status: boolean, data?: {status: string, data?: object, message?: string}, message?: string, error?: string}>}
 */
export const checkDiscoveryJobStatus = async (token, jobId) => {
  const url = `${baseUrl}/discovery/api/search-status/${jobId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      // REFACTORED: The backend now reliably returns a 200 OK for a successful status check
      // (even if the job itself failed), or a 404 if the job is not found.
      // We let axios handle standard success/error ranges.
      validateStatus: (status) => status >= 200 && status < 300,
    });

    // The request itself was successful (200 OK). The job's status is in the data.
    return { status: true, data: response.data };
  } catch (error) {
    // This will catch 4xx/5xx errors from axios, including our 404 for a missing job.
    return handleApiError(error, 'checkDiscoveryJobStatus');
  }
};

export const cancelDiscoveryJob = async (token, jobId) => {
  // FIXED: Added '/api' to the path to match your Blueprint config
  const url = `${baseUrl}/discovery/api/jobs/${jobId}/cancel`;

  try {
    const response = await axios.post(
      url,
      {}, // Empty body for POST request
      {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => status >= 200 && status < 300,
      },
    );

    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'cancelDiscoveryJob');
  }
};

/**
 * Performs a deep search for potential influencers, returning them categorized.
 * The backend now also checks against the on-hold list.
 * @param {string} token - The user's Firebase auth token.
 * @param {object} searchParams - The search parameters.
 * @returns {Promise<{status: boolean, data?: {newInfluencers: object[], existingYouTubeInfluencers: object[], similarInfluencers: object[], discardedInfluencers: object[], onHoldInfluencers: object[]}, message?: string, error?: string}>}
 */
export const searchDiscovery = async (token, searchParams) => {
  const url = `${baseUrl}/discovery/api/search`;
  console.debug(
    '[DiscoveryPageApi.searchDiscovery] Firing search with params:',
    searchParams,
  );

  try {
    const response = await axios.post(url, searchParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status >= 200 && response.status < 300) {
      console.debug(`[DiscoveryPageApi.searchDiscovery] Search successful.`);
      return { status: true, data: response.data };
    } else {
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'searchDiscovery');
  }
};

/**
 * Moves a list of discovered influencers to the "On-Hold" evaluation list.
 * @param {string} token - The user's Firebase auth token.
 * @param {Array<object>} influencersToHold - An array of full influencer objects to place on hold.
 * @returns {Promise<{status: boolean, data?: {message: string, on_hold_influencers: object[]}, message?: string, error?: string}>}
 */
export const moveInfluencersToHold = async (token, influencersToHold) => {
  const url = `${baseUrl}/discovery/api/hold`;
  const payload = { influencers: influencersToHold };
  console.debug(
    '[DiscoveryPageApi.moveInfluencersToHold] Firing request with payload:',
    payload,
  );

  try {
    const response = await axios.post(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, data: response.data };
    } else {
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'moveInfluencersToHold');
  }
};

/**
 * Sends a request to the backend to permanently exclude a list of influencers from future searches.
 * @param {string} token - The user's Firebase auth token.
 * @param {Array<object>} discards - An array of influencer objects to discard.
 * @returns {Promise<{status: boolean, data?: {message: string, discarded_influencers: object[]}, message?: string, error?: string}>}
 */
export const permanentlyDiscardInfluencers = async (token, discards) => {
  const url = `${baseUrl}/discovery/api/permanently-discard`;
  const payload = { discards };
  console.debug(
    '[DiscoveryPageApi.permanentlyDiscardInfluencers] Firing discard with payload:',
    payload,
  );

  try {
    const response = await axios.post(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, data: response.data };
    } else {
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'permanentlyDiscardInfluencers');
  }
};

/**
 * Sends a request to the backend to import a list of influencers from either the 'new' or 'on_hold' source.
 * @param {string} token - The user's Firebase auth token.
 * @param {string} platform - The platform being imported from, e.g., 'youtube'.
 * @param {Array<object>} importRequests - An array of objects, each containing the full influencer data.
 * @param {string} source - 'new' or 'on_hold' to indicate where the import is coming from.
 * @returns {Promise<{status: boolean, data?: {message: string, failedImports: object[]}, message?: string, error?: string}>}
 */
export const importDiscoveredInfluencers = async (
  token,
  platform,
  importRequests,
  source,
) => {
  const url = `${baseUrl}/discovery/api/import`;
  const payload = { platform, importRequests, source };
  console.debug(
    '[DiscoveryPageApi.importDiscoveredInfluencers] Firing import with payload:',
    payload,
  );

  try {
    const response = await axios.post(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 202) {
      return { status: true, data: response.data };
    } else {
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'importDiscoveredInfluencers');
  }
};

/**
 * Triggers a BATCH lightweight, non-LLM health refresh for all of the user's on-hold influencers.
 * @param {string} token - Firebase auth token.
 * @returns {Promise<{status: boolean, data?: {message: string, updated_influencers: object[], failed_refreshes: object[]}, message?: string, error?: string}>}
 */
export const batchRefreshOnHoldInfluencers = async (token) => {
  const url = `${baseUrl}/discovery/api/hold/batch-refresh`;
  console.debug(
    '[DiscoveryPageApi.batchRefreshOnHoldInfluencers] Firing request.',
  );
  try {
    const response = await axios.post(
      url,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => status >= 200 && status < 500,
      },
    );

    if (response.status === 200) {
      return { status: true, data: response.data };
    } else {
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'batchRefreshOnHoldInfluencers');
  }
};

/**
 * Triggers a full, individual LLM-based comparative analysis for a single on-hold influencer.
 * @param {string} token - Firebase auth token.
 * @param {number} onHoldId - The database ID of the on-hold influencer.
 * @returns {Promise<{status: boolean, data?: object, message?: string, error?: string}>} The full dashboard data.
 */
export const analyzeOnHoldInfluencer = async (token, onHoldId) => {
  const url = `${baseUrl}/discovery/api/hold/${onHoldId}/analyze`;
  console.debug(
    `[DiscoveryPageApi.analyzeOnHoldInfluencer] Firing request for on-hold ID: ${onHoldId}`,
  );
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, data: response.data };
    } else {
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'analyzeOnHoldInfluencer');
  }
};

/**
 * Triggers a SINGLE lightweight, non-LLM health refresh for one on-hold influencer.
 * @param {string} token - Firebase auth token.
 * @param {number} onHoldId - The database ID of the on-hold influencer to refresh.
 * @returns {Promise<{status: boolean, data?: object, message?: string, error?: string}>} The updated influencer object.
 */
export const singleRefreshOnHoldInfluencer = async (token, onHoldId) => {
  const url = `${baseUrl}/discovery/api/hold/${onHoldId}/refresh`;
  console.debug(
    `[DiscoveryPageApi.singleRefreshOnHoldInfluencer] Firing request for on-hold ID: ${onHoldId}`,
  );
  try {
    // This is a POST request as it modifies data on the server.
    const response = await axios.post(
      url,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => status >= 200 && status < 500,
      },
    );

    if (response.status === 200) {
      return { status: true, data: response.data };
    } else {
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`,
      };
    }
  } catch (error) {
    return handleApiError(error, 'singleRefreshOnHoldInfluencer');
  }
};
